import React, { useMemo } from 'react';
import { connect, useSelector } from 'react-redux';
import { Button, Col, Modal, Row, Nav, Tab, Badge } from 'react-bootstrap-5';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBank,
  faBriefcase,
  faBuilding,
  faBuildings,
  faBuildingUser,
  faCube,
  faSparkle,
} from '@fortawesome/pro-solid-svg-icons';

import { setFilterAttribute } from 'bundles/connect/components/store/actions/criteria';

import PropertyTypeInput from './inputs/property_type_input';
import BuildingClassInput from './inputs/building_class_input';
import MultiSelectInput from './inputs/multi_select_input';
import MultiSelectDropdown from './inputs/multi_select_dropdown_input';
import RangeInput from './inputs/range_input';
import DateRangeInput from './inputs/date_range_input';
import RegionInput from './inputs/region_input';
import TextInput from './inputs/text_input';
import Toggle from './inputs/toggle';


import {
  AIR_CONDITIONING_CODES,
  BUILDING_CONDITIONS,
  LIKELY_TO_SELL_RANKS,
  ROOF_TYPES,
  OFF_MARKET_LOAN_TYPES,
  ROOF_COVER_TYPES,
  PROPERTY_INFO_FILTER_INDEX,
  BUILDING_INFO_FILTER_INDEX,
  LOT_INFO_FILTER_INDEX,
  OWNER_INFO_FILTER_INDEX,
  FINANCIAL_INFO_FILTER_INDEX,
  TENANT_INFO_FILTER_INDEX
} from './constants';
import { TENANT_EMPLOYEE_SIZE_MAP, TENANT_SALES_REVENUE_MAP, TENANT_SIC_CODE_MAP } from '../../../../constants';
import { map } from 'lodash';
import { getStateValue, STATES } from 'components/state_select';
import BooleanSelect from './inputs/boolean_select';
import RethinkMode from '../../../rethink_mode/rethink_mode';
import type { Product } from 'bundles/connect/types';
import Filter from './filter';
import { faWandSparkles } from '@fortawesome/pro-regular-svg-icons';
import { selectCriteria } from 'bundles/connect/components/store/selectors/criteria';
import { getNumActiveFiltersByGroup } from './helpers';

type OffMarketFiltersModalProps = {
  products: Product["products"];
  setFilterAttribute: (attribute: string, value: any, options?: any) => { type: string } | undefined;
  show: boolean;
  onHide: () => void;
  choicePresets?: any;
}

function OffMarketFiltersModal({
  products,
  setFilterAttribute,
  show,
  onHide,
  choicePresets,
}: OffMarketFiltersModalProps) {
  const criteria = useSelector(selectCriteria);
  const sicCodeOptions = useMemo(() => map(TENANT_SIC_CODE_MAP, (k, v) => ({ label: `${v} - ${k}`, value: v })), []);
  const hasProspecting = !!products?.prospecting;
  const filterLengthsByGroup = getNumActiveFiltersByGroup(criteria);

  return (
    <Modal
      centered
      className="search-filters"
      fullscreen="sm-down"
      scrollable={true}
      show={show}
      size="lg"
      onHide={onHide}
    >
      <Modal.Header closeButton>
        <Modal.Title as="h5">Filters</Modal.Title>
      </Modal.Header>
      <Modal.Body className="bg-white p-0">
        {!hasProspecting && (
          <div className="bg-light p-4">
            <Row>
              <Col xs="auto">
                <FontAwesomeIcon className="pt-2" icon={faWandSparkles} />
              </Col>
              <Col>
                <Row className="align-items-center gy-2">
                  <Col>
                    <div className="fw-bolder">Get Access to Powerful Filtering</div>
                    <small>
                      Upgrade your account to unlock all of our filters including our predictive analytics filters.
                    </small>
                  </Col>
                  <Col md="auto" xs={12}>
                    <Button className="text-nowrap rounded-2" size="sm" variant="secondary">
                      <FontAwesomeIcon icon={faSparkle} />
                      Upgrade Account
                    </Button>
                  </Col>
                </Row>
              </Col>
            </Row>
          </div>
        )}
        <Tab.Container defaultActiveKey="property-information">
          <Row className="gx-0" style={{ height: '70vh', maxHeight: '1000px' }}>
            <Col className="overflow-x-auto d-flex flex-column" md="auto" sm={12} style={{ minWidth: '250px' }}>
              <Nav className="flex-md-column flex-nowrap nav-accent p-4" variant="pills">
                <Nav.Item className="text-nowrap">
                  <Nav.Link className="text-truncate text-start d-flex align-items-center" eventKey="property-information">
                    <FontAwesomeIcon className="me-2" icon={faBuildings} />
                    Property Information
                    {filterLengthsByGroup[PROPERTY_INFO_FILTER_INDEX] > 0 && (
                      <Badge className="filter-count-pill ms-md-auto ms-2 fs-x-small text-bg-danger" bg="none" pill>
                        {filterLengthsByGroup[PROPERTY_INFO_FILTER_INDEX]}
                      </Badge>
                    )}
                  </Nav.Link>
                </Nav.Item>
                <Nav.Item className="text-nowrap">
                  <Nav.Link className="text-truncate text-start d-flex align-items-center" eventKey="building-information">
                    <FontAwesomeIcon className="me-2" icon={faBuilding} />
                    Building Information
                    {filterLengthsByGroup[BUILDING_INFO_FILTER_INDEX] > 0 &&
                      <Badge className="filter-count-pill ms-md-auto ms-2 fs-x-small text-bg-danger" bg="none" pill>
                        {filterLengthsByGroup[BUILDING_INFO_FILTER_INDEX]}
                      </Badge>
                    }
                  </Nav.Link>
                </Nav.Item>
                <Nav.Item className="text-nowrap">
                  <Nav.Link className="text-truncate text-start d-flex align-items-center" eventKey="lot-information">
                    <FontAwesomeIcon className="me-2" icon={faCube} />
                    Lot Information
                    {filterLengthsByGroup[LOT_INFO_FILTER_INDEX] > 0 &&
                      <Badge className="filter-count-pill ms-md-auto ms-2 fs-x-small text-bg-danger" bg="none" pill>
                        {filterLengthsByGroup[LOT_INFO_FILTER_INDEX]}
                      </Badge>
                    }
                  </Nav.Link>
                </Nav.Item>
                <Nav.Item className="text-nowrap">
                  <Nav.Link className="text-truncate text-start d-flex align-items-center" eventKey="owner-information">
                    <FontAwesomeIcon className="me-2" icon={faBuildingUser} />
                    Owner Information
                    {filterLengthsByGroup[OWNER_INFO_FILTER_INDEX] > 0 &&
                      <Badge className="filter-count-pill ms-md-auto ms-2 fs-x-small text-bg-danger" bg="none" pill>
                        {filterLengthsByGroup[OWNER_INFO_FILTER_INDEX]}
                      </Badge>
                    }
                  </Nav.Link>
                </Nav.Item>
                <Nav.Item className="text-nowrap">
                  <Nav.Link className="text-truncate text-start d-flex align-items-center" eventKey="financial-information">
                    <FontAwesomeIcon className="me-2" icon={faBank} />
                    Financial Information
                    {filterLengthsByGroup[FINANCIAL_INFO_FILTER_INDEX] > 0 &&
                      <Badge className="filter-count-pill ms-md-auto ms-2 fs-x-small text-bg-danger" bg="none" pill>
                        {filterLengthsByGroup[FINANCIAL_INFO_FILTER_INDEX]}
                      </Badge>
                    }
                  </Nav.Link>
                </Nav.Item>
                <Nav.Item className="text-nowrap">
                  <Nav.Link className="text-truncate text-start d-flex align-items-center" eventKey="tenant-information">
                    <FontAwesomeIcon className="me-2" icon={faBriefcase} />
                    Tenant Information
                    {filterLengthsByGroup[TENANT_INFO_FILTER_INDEX] > 0 &&
                      <Badge className="filter-count-pill ms-md-auto ms-2 fs-x-small text-bg-danger" bg="none" pill>
                        {filterLengthsByGroup[TENANT_INFO_FILTER_INDEX]}
                      </Badge>
                    }
                  </Nav.Link>
                </Nav.Item>
              </Nav>
            </Col>
            <Col className="d-flex flex-column h-100" md sm={12}>
              <Tab.Content className="p-4 flex-grow-1 overflow-auto">
                <Tab.Pane eventKey="property-information">
                  <div className="d-flex flex-column gap-3">
                    <Filter className="border rounded overflow-hidden" title="Property Type">
                      <PropertyTypeInput />
                    </Filter>
                    <div>
                      <Toggle
                        checked={!!criteria?.onMarket}
                        label="Active Listings"
                        onClick={() => setFilterAttribute('onMarket', !criteria?.onMarket)}
                      />
                      <Toggle
                        checked={!!criteria?.minLikelyToSellRank}
                        disabled={!hasProspecting}
                        label="Likely to Sell Only"
                        onClick={() => {
                          setFilterAttribute(
                            'minLikelyToSellRank',
                            criteria?.minLikelyToSellRank ? undefined : LIKELY_TO_SELL_RANKS[1]
                          );
                        }}
                      />
                      <Toggle
                        checked={!!criteria?.minLikelyToRefiRank}
                        disabled={!hasProspecting}
                        label="Likely to Refi Only"
                        onClick={() => {
                          setFilterAttribute(
                            'minLikelyToRefiRank',
                            criteria?.minLikelyToRefiRank ? undefined : LIKELY_TO_SELL_RANKS[1]
                          );
                        }}
                      />
                      <RethinkMode>
                        <Toggle
                          checked={!!criteria?.bookmarked}
                          disabled={!hasProspecting}
                          label="Property Bookmarked"
                          onClick={() => setFilterAttribute('bookmarked', !criteria.bookmarked)}
                        />
                      </RethinkMode>
                    </div>
                    <Filter title="Region">
                      <RegionInput />
                    </Filter>
                    <Toggle
                      checked={!!criteria?.qoz}
                      disabled={!hasProspecting}
                      label="Opportunity Zone"
                      onClick={() => setFilterAttribute('qoz', !criteria?.qoz)}
                    />
                    <Filter title="APN">
                      <TextInput attribute="apn" placeholder="Enter an APN" />
                    </Filter>
                  </div>
                </Tab.Pane>
                <Tab.Pane eventKey="building-information">
                  <div className="d-flex flex-column gap-3">
                    <Filter title="Building SF">
                      <RangeInput
                        maxAttribute="maxBuildingSizeSf"
                        minAttribute="minBuildingSizeSf"
                        placeholder="SF"
                        type="number"
                      />
                    </Filter>
                    <Filter title="Number of Units">
                      <RangeInput
                        maxAttribute="maxNumberOfUnits"
                        minAttribute="minNumberOfUnits"
                        placeholder=""
                        type="number"
                      />
                    </Filter>
                    <Filter title="Number of Residential Units">
                      <RangeInput
                        maxAttribute="maxResidentialUnits"
                        minAttribute="minResidentialUnits"
                        placeholder=""
                        type="number"
                      />
                    </Filter>
                    <Filter title="Number of Commercial Units">
                      <RangeInput
                        maxAttribute="maxCommercialUnits"
                        minAttribute="minCommercialUnits"
                        placeholder=""
                        type="number"
                      />
                    </Filter>
                    <Filter title="Number of Buildings">
                      <RangeInput
                        maxAttribute="maxNumberOfBuildings"
                        minAttribute="minNumberOfBuildings"
                        placeholder=""
                        type="number"
                      />
                    </Filter>
                    <Filter title="Number of Stories">
                      <RangeInput
                        maxAttribute="maxNumberOfStories"
                        minAttribute="minNumberOfStories"
                        placeholder=""
                      />
                    </Filter>
                    {criteria?.assetClassIds?.includes(10) && (
                      <>
                        <Filter title="Number of Bedrooms">
                          <RangeInput
                            maxAttribute="maxNumberOfBedrooms"
                            minAttribute="minNumberOfBedrooms"
                            placeholder=""
                          />
                        </Filter>
                        <Filter title="Number of Bathrooms">
                          <RangeInput
                            maxAttribute="maxNumberOfBathrooms"
                            minAttribute="minNumberOfBathrooms"
                            placeholder=""
                          />
                        </Filter>
                      </>
                    )}
                    <Filter title="Floor Area Ratio">
                      <RangeInput
                        float
                        maxAttribute="maxFloorAreaRatio"
                        minAttribute="minFloorAreaRatio"
                        placeholder=""
                      />
                    </Filter>
                    <Filter title="Year Built">
                      <RangeInput
                        maxAttribute="maxYearBuilt"
                        minAttribute="minYearBuilt"
                        placeholder=""
                        type="number"
                      />
                    </Filter>
                    <Filter title="Year Renovated">
                      <RangeInput
                        maxAttribute="maxYearRenovated"
                        minAttribute="minYearRenovated"
                        placeholder=""
                        type="number"
                      />
                    </Filter>
                    <Filter disabled={!hasProspecting} title="Roof Type">
                      <MultiSelectDropdown
                        attribute="roofType"
                        disabled={!hasProspecting}
                        options={ROOF_TYPES.map(i => ({
                          label: i,
                          value: i,
                        }))}
                      />
                    </Filter>
                    <Filter disabled={!hasProspecting} title="Roof Cover Type">
                      <MultiSelectDropdown
                        attribute="roofCoverType"
                        disabled={!hasProspecting}
                        options={ROOF_COVER_TYPES.map(i => ({
                          label: i,
                          value: i,
                        }))}
                      />
                    </Filter>
                    <Filter disabled={!hasProspecting} title="Air Conditioning Code">
                      <MultiSelectDropdown
                        attribute="airConditioningId"
                        disabled={!hasProspecting}
                        options={AIR_CONDITIONING_CODES.map(i => ({
                          label: i.toString(),
                          value: i,
                        }))}
                      />
                    </Filter>
                    <Filter disabled={!hasProspecting} title="Building Condition">
                      <MultiSelectDropdown
                        attribute="buildingCondition"
                        disabled={!hasProspecting}
                        options={BUILDING_CONDITIONS.map(i => ({
                          label: i,
                          value: i,
                        }))}
                      />
                    </Filter>
                    <Filter bordered disabled={!hasProspecting} title="Building Class">
                      <BuildingClassInput disabled={!hasProspecting} />
                    </Filter>
                  </div>
                </Tab.Pane>
                <Tab.Pane eventKey="lot-information">
                  <div className="d-flex flex-column gap-3">
                    <Filter title="Lot Size">
                      <RangeInput
                        float
                        maxAttribute="maxLotSizeAcres"
                        minAttribute="minLotSizeAcres"
                        placeholder="Acres"
                      />
                    </Filter>
                    <Filter title="Lot Depth">
                      <RangeInput
                        maxAttribute="maxLotDepth"
                        minAttribute="minLotDepth"
                        placeholder="Ft"
                        type="number"
                      />
                    </Filter>
                    <Filter title="Lot Frontage">
                      <RangeInput
                        maxAttribute="maxLotFrontage"
                        minAttribute="minLotFrontage"
                        placeholder="Ft"
                        type="number"
                      />
                    </Filter>

                    <Filter title="Zoning">
                      <>
                        {(choicePresets?.zoneList && choicePresets.zoneList.length > 0) ?
                          (
                            <MultiSelectDropdown
                              attribute="zoning"
                              options={choicePresets.zoneList.map(i => ({
                                label: i,
                                value: i,
                              }))}
                            />
                          ) : (
                            <TextInput
                              attribute="zoning"
                              placeholder="Enter a zoning code"
                            />
                          )}
                      </>
                    </Filter>
                  </div>
                </Tab.Pane>
                <Tab.Pane eventKey="owner-information">
                  <div className="d-flex flex-column gap-3">
                    <Filter disabled={!hasProspecting} title="Owner Name">
                      <TextInput
                        attribute="ownerName"
                        disabled={!hasProspecting}
                        placeholder="Enter the owner's name"
                      />
                    </Filter>
                    <Filter disabled={!hasProspecting} title="Corporate Owned">
                      <BooleanSelect
                        attribute="corporateOwned"
                        disabled={!hasProspecting}
                        includeBlank={true}
                      />
                    </Filter>
                    <Filter disabled={!hasProspecting} title="Occupied by Owner">
                      <BooleanSelect
                        attribute="ownerOccupied"
                        disabled={!hasProspecting}
                        includeBlank={true}
                      />
                    </Filter>
                    <Filter disabled={!hasProspecting} title="Owner Address">
                      <TextInput
                        attribute="ownerAddress"
                        disabled={!hasProspecting}
                        placeholder="Enter the owner's address"
                      />
                    </Filter>
                    <Filter disabled={!hasProspecting} title="Owner State">
                      <MultiSelectDropdown
                        attribute="ownerState"
                        disabled={!hasProspecting}
                        options={
                          Object.keys(STATES).map(code => ({
                            value: code,
                            label: getStateValue(code)
                          }))
                        }
                      />
                    </Filter>
                    <Filter disabled={!hasProspecting} title="Owner City">
                      <TextInput
                        attribute="ownerCity"
                        disabled={!hasProspecting}
                        placeholder="Enter the owner's city"
                      />
                    </Filter>
                    <Filter disabled={!hasProspecting} title="Owner Zip">
                      <TextInput
                        attribute="ownerZip"
                        disabled={!hasProspecting}
                        maxLength={5}
                        placeholder="Enter the owner's zip code"
                      />
                    </Filter>
                  </div>
                </Tab.Pane>
                <Tab.Pane eventKey="financial-information">
                  <div className="d-flex flex-column gap-3">
                    <Filter disabled={!hasProspecting} title="Land Assessed Value">
                      <RangeInput
                        disabled={!hasProspecting}
                        maxAttribute="maxLandAssessedValue"
                        minAttribute="minLandAssessedValue"
                        placeholder=""
                        type="currency"
                      />
                    </Filter>
                    <Filter disabled={!hasProspecting} title="Improvement Assessed Value">
                      <RangeInput
                        disabled={!hasProspecting}
                        maxAttribute="maxImprovementAssessedValue"
                        minAttribute="minImprovementAssessedValue"
                        placeholder=""
                        type="currency"
                      />
                    </Filter>
                    <Filter disabled={!hasProspecting} title="Total Assessed Value">
                      <RangeInput
                        disabled={!hasProspecting}
                        maxAttribute="maxTotalAssessedValue"
                        minAttribute="minTotalAssessedValue"
                        placeholder=""
                        type="currency"
                      />
                    </Filter>
                    <Filter disabled={!hasProspecting} title="Tax Amount">
                      <RangeInput
                        disabled={!hasProspecting}
                        maxAttribute="maxTaxAmount"
                        minAttribute="minTaxAmount"
                        placeholder=""
                        type="currency"
                      />
                    </Filter>
                    <Filter disabled={!hasProspecting} title="Last Transaction Amount">
                      <RangeInput
                        disabled={!hasProspecting}
                        maxAttribute="maxLastTransactionAmount"
                        minAttribute="minLastTransactionAmount"
                        placeholder=""
                        type="currency"
                      />
                    </Filter>
                    <Filter disabled={!hasProspecting} title="Last Transaction Amount per SF">
                      <RangeInput
                        disabled={!hasProspecting}
                        maxAttribute="maxLastTransactionAmountPerSf"
                        minAttribute="minLastTransactionAmountPerSf"
                        placeholder=""
                        type="currency"
                      />
                    </Filter>
                    <Filter disabled={!hasProspecting} title="Tax Delinquent Year">
                      <RangeInput
                        disabled={!hasProspecting}
                        maxAttribute="maxTaxDelinquentYear"
                        minAttribute="minTaxDelinquentYear"
                        placeholder=""
                        type="number"
                      />
                    </Filter>
                    <Filter disabled={!hasProspecting} title="Last Transaction Date">
                      <DateRangeInput
                        disabled={!hasProspecting}
                        maxAttribute="maxTransactionDate"
                        minAttribute="minTransactionDate"
                        placeholder=""
                      />
                    </Filter>
                    <Filter disabled={!hasProspecting} title="Loan Amount">
                      <RangeInput
                        disabled={!hasProspecting}
                        maxAttribute="maxLoanAmount"
                        minAttribute="minLoanAmount"
                        placeholder=""
                        type="currency"
                      />
                    </Filter>
                    <Filter disabled={!hasProspecting} title="Loan Type">
                      <MultiSelectDropdown
                        attribute="loanType"
                        disabled={!hasProspecting}
                        options={OFF_MARKET_LOAN_TYPES.map(i => ({
                          label: i,
                          value: i,
                        }))}
                      />
                    </Filter>
                    <Filter disabled={!hasProspecting} title="Lender Name">
                      <TextInput
                        attribute="lenderName"
                        disabled={!hasProspecting}
                        placeholder="Enter the lender name"
                      />
                    </Filter>
                    <Filter disabled={!hasProspecting} title="Loan Interest Rate">
                      <RangeInput
                        disabled={!hasProspecting}
                        maxAttribute="maxLoanInterestRate"
                        minAttribute="minLoanInterestRate"
                        percent
                        placeholder=""
                      />
                    </Filter>
                    <Filter disabled={!hasProspecting} title="Loan Starting Date">
                      <DateRangeInput
                        disabled={!hasProspecting}
                        maxAttribute="maxLoanBeginningDate"
                        minAttribute="minLoanBeginningDate"
                        placeholder=""
                      />
                    </Filter>
                    <Filter disabled={!hasProspecting} title="Loan Maturity Date">
                      <DateRangeInput
                        disabled={!hasProspecting}
                        maxAttribute="maxLoanMaturityDate"
                        minAttribute="minLoanMaturityDate"
                        placeholder=""
                      />
                    </Filter>
                    <Filter disabled={!hasProspecting} title="Loan Reset Date">
                      <DateRangeInput
                        disabled={!hasProspecting}
                        maxAttribute="maxLoanResetDate"
                        minAttribute="minLoanResetDate"
                        placeholder=""
                      />
                    </Filter>
                  </div>
                </Tab.Pane>
                <Tab.Pane eventKey="tenant-information">
                  <div className="d-flex flex-column gap-3">
                    <Filter disabled={!hasProspecting} title="Tenant Company Name">
                      <TextInput
                        attribute="tenantCompanyName"
                        disabled={!hasProspecting}
                        placeholder="Enter a company name"
                      />
                    </Filter>
                    <Filter bordered disabled={!hasProspecting} title="Tenant Employee Size">
                      <MultiSelectInput
                        criteriaKey="tenantEmployeeSizes"
                        disabled={!hasProspecting}
                        options={TENANT_EMPLOYEE_SIZE_MAP}
                      />
                    </Filter>
                    <Filter bordered disabled={!hasProspecting} title="Tenant Sales Revenue">
                      <MultiSelectInput
                        criteriaKey="tenantSalesRevenues"
                        disabled={!hasProspecting}
                        options={TENANT_SALES_REVENUE_MAP}
                      />
                    </Filter>
                    <Filter disabled={!hasProspecting} title="Tenant SIC Codes">
                      <MultiSelectDropdown
                        attribute="tenantSicCodes"
                        disabled={!hasProspecting}
                        isMulti
                        options={sicCodeOptions}
                      />
                    </Filter>
                    <Filter disabled={!hasProspecting} title="Number of Tenants">
                      <RangeInput
                        disabled={!hasProspecting}
                        maxAttribute="maxTenantsCount"
                        minAttribute="minTenantsCount"
                        placeholder=""
                        type="number"
                      />
                    </Filter>
                  </div>
                </Tab.Pane>
              </Tab.Content>
            </Col>
          </Row>
        </Tab.Container>
      </Modal.Body>
      <Modal.Footer className="p-2 justify-content-start justify-content-md-end">
        <Button variant="text-primary" onClick={onHide}>
          Close
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

const mapStateToProps = ({ ui: { choicePresets, products } }) => ({ choicePresets, products });

export default connect(mapStateToProps, { setFilterAttribute })(
  OffMarketFiltersModal
);
